import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { OrderStatusEnum } from '../../utils/enums/OrderStatusEnum';
import { OrderTypeEnum } from '../../utils/enums/OrderTypeEnum';
import { lineAmount } from '../../utils/orderLine';
import { readyStockSummary } from './OrderInvoice';
import { splitOrdersByStock, getOrderStockCategory, orderPlacementSummary } from '../../utils/orderStock';
import './registerInvoiceFonts';

// ── Formatters ──────────────────────────────────────────────────────────────
const money = (n) => 'Tk ' + Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtNow = () => dayjs().format('DD MMMM YYYY, h:mm A');

// ── Palette — same values as OrderInvoice so the two read as one document ──
const C = {
  text: '#1a1a1a',
  muted: '#6b6b6b',
  line: '#ececec',
  lineStrong: '#d8d8d8',
  headBg: '#f2f2f2',
  orderBg: '#f7f7f7',
  courier: '#0b7a3b',
};

// ── Styles ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'CourierPrime',
    color: C.text,
    fontSize: 9,
    lineHeight: 1.4,
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 36,
  },
  bangla: { fontFamily: 'HindSiliguri' },

  // Title block.
  titleBlock: { marginBottom: 12 },
  title: { fontSize: 16, fontWeight: 700, letterSpacing: 1 },
  subtitle: { fontSize: 8.5, color: C.muted, marginTop: 2 },
  rule: { height: 1.5, backgroundColor: C.text, marginTop: 10, marginBottom: 10 },

  // Order header row.
  orderRow: {
    flexDirection: 'row',
    backgroundColor: C.orderBg,
    borderBottomWidth: 1,
    borderBottomColor: C.lineStrong,
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  orderLabel: { fontSize: 8.5, fontWeight: 700 },
  orderMuted: { fontSize: 8, color: C.muted },

  // Product line row.
  productRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: C.line,
    paddingLeft: 14,
  },
  ptd: {
    paddingVertical: 3.5,
    paddingHorizontal: 4,
    fontSize: 8,
  },

  // Section headers for Ready Stock vs Production separation
  sectionHeader: {
    backgroundColor: '#e6f4ea',
    borderLeftWidth: 3,
    borderLeftColor: C.courier,
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    marginTop: 8,
    marginBottom: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 8.5,
    fontWeight: 700,
    color: C.courier,
    letterSpacing: 0.5,
  },
  sectionSubtotal: {
    fontSize: 8,
    fontWeight: 700,
    color: C.courier,
  },
  sectionHeaderProd: {
    backgroundColor: '#fdf2f8',
    borderLeftWidth: 3,
    borderLeftColor: '#9d174d',
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    marginTop: 10,
    marginBottom: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitleProd: {
    fontSize: 8.5,
    fontWeight: 700,
    color: '#9d174d',
    letterSpacing: 0.5,
  },
  sectionSubtotalProd: {
    fontSize: 8,
    fontWeight: 700,
    color: '#9d174d',
  },
  stockTag: {
    fontSize: 6.5,
    fontWeight: 700,
    marginTop: 1,
  },

  // Footer.
  footerRow: {
    borderTopWidth: 2,
    borderTopColor: C.text,
    marginTop: 4,
    paddingTop: 6,
    paddingHorizontal: 5,
  },
  footerLabel: { fontWeight: 700, fontSize: 9 },

  pageFooter: {
    position: 'absolute',
    bottom: 14,
    left: 36,
    right: 36,
    fontSize: 7.5,
    color: C.muted,
    textAlign: 'center',
  },
});

// ── Column widths ───────────────────────────────────────────────────────────

// Order header: Order# | Channel | Submitted By | Status | Total Price
const OW = {
  no:          '14%',
  channel:     '25%',
  submittedBy: '25%',
  status:      '18%',
  totalPrice:  '18%',
};

// Product lines (indented): Product info | Amount
const PW = {
  info:   '82%',
  amount: '18%',
};

// ── Row builders (exported for tests) ───────────────────────────────────────

/**
 * Calculate the total amount for an order from any available representation.
 */
export const orderTotal = (order) =>
  order.payment?.totalAmount ??
  order.totalAmount ??
  order.total_amount ??
  (order.products || []).reduce((sum, p) => sum + lineAmount(p), 0);

/**
 * The channel label: marketplace page name for online orders, "Offline" for
 * merchant (over-the-counter) orders.
 */
export const channelLabel = (order) =>
  order.orderType === OrderTypeEnum.MARKETPLACE
    ? (order.marketplace?.name || 'Online')
    : 'Offline';

/**
 * Build the summary row data for one order.
 */
export const summaryRow = (order) => ({
  no: order.clientOrderNumber ? `#${order.id} (${order.clientOrderNumber})` : `#${order.id}`,
  channel: channelLabel(order),
  submittedBy: order.submittedBy || '—',
  status: OrderStatusEnum.find((x) => x.value === order.status)?.label || '—',
  totalPrice: money(orderTotal(order)),
  stockCategory: getOrderStockCategory(order),
  placement: orderPlacementSummary(order),
});

/**
 * Product type + fabric type + colour + pattern in one string.
 *
 * Joins the four terms with " · " and drops any that are absent, so an order
 * placed before the pattern book existed just shows three terms.
 */
export const productInfo = (p) => [
  p.productType?.name,
  p.fabricType?.name,
  p.fabrics?.name,
  p.fabricPattern?.name,
].filter(Boolean).join(' · ') || '—';

/**
 * Build one product line's display data.
 */
export const productLine = (p) => ({
  info: productInfo(p),
  amount: money(lineAmount(p)),
  readyStock: readyStockSummary(p),
});

// ── Bangla ascent lift ──────────────────────────────────────────────────────
const BANGLA_ASCENT_LIFT = 1116 / 1000 - 1600 / 2048;

const bangla = (fontSize) => {
  if (!fontSize) return s.bangla;
  const lift = BANGLA_ASCENT_LIFT * fontSize;
  return { ...s.bangla, marginTop: -lift, marginBottom: lift };
};

// ── Component ───────────────────────────────────────────────────────────────

const renderOrderGroup = (order, oi, listLength) => {
  const row = summaryRow(order);
  const products = (order.products || []).map(productLine);

  return (
    <View key={order.id} wrap={false} style={{ marginBottom: oi < listLength - 1 ? 6 : 0 }}>
      {/* Order header */}
      <View style={s.orderRow}>
        <View style={{ width: OW.no }}>
          <Text style={s.orderLabel}>{row.no}</Text>
          {row.stockCategory === 'FULL' && (
            <Text style={[s.stockTag, { color: C.courier }]}>[READY STOCK]</Text>
          )}
          {row.stockCategory === 'PARTIAL' && (
            <Text style={[s.stockTag, { color: '#b45309' }]}>[PARTIAL STOCK]</Text>
          )}
        </View>
        <Text style={[s.orderMuted, bangla(8.5), { width: OW.channel }]}>{row.channel}</Text>
        <Text style={[s.orderMuted, bangla(8.5), { width: OW.submittedBy }]}>{row.submittedBy}</Text>
        <Text style={[s.orderMuted, { width: OW.status }]}>{row.status}</Text>
        <Text style={[s.orderLabel, { width: OW.totalPrice, textAlign: 'right' }]}>{row.totalPrice}</Text>
      </View>

      {/* Product lines */}
      {products.map((p, pi) => (
        <View key={pi} style={s.productRow}>
          <Text style={[s.ptd, bangla(8), { width: PW.info }]}>
            {p.info}
            {p.readyStock ? (
              <Text style={{ color: C.courier, fontWeight: 700 }}>{`  ${p.readyStock}`}</Text>
            ) : null}
          </Text>
          <Text style={[s.ptd, { width: PW.amount, textAlign: 'right' }]}>{p.amount}</Text>
        </View>
      ))}
    </View>
  );
};

/**
 * A summary listing every order in a bulk print job.
 *
 * Automatically separates Ready Stock orders from Production orders into two
 * clear sections with subtotals when a batch is mixed.
 */
const PrintSummaryPage = ({ orders = [] }) => {
  const { readyStockOrders, productionOrders } = splitOrdersByStock(orders);
  const isMixed = readyStockOrders.length > 0 && productionOrders.length > 0;

  return (
    <Page size="A4" style={s.page}>

      {/* Title */}
      <View style={s.titleBlock}>
        <Text style={s.title}>PRINT SUMMARY</Text>
        <Text style={s.subtitle}>
          {fmtNow()}  ·  {orders.length} order{orders.length === 1 ? '' : 's'}
          {isMixed ? `  (${readyStockOrders.length} ready stock, ${productionOrders.length} production)` : ''}
        </Text>
      </View>

      <View style={s.rule} />

      {/* Section 1: Ready Stock Orders */}
      {readyStockOrders.length > 0 && (
        <View style={{ marginBottom: isMixed ? 8 : 0 }}>
          {isMixed && (
            <View wrap={false} style={s.sectionHeader}>
              <Text style={s.sectionTitle}>
                READY STOCK / SHELF PICKUP ({readyStockOrders.length} order{readyStockOrders.length === 1 ? '' : 's'})
              </Text>
              <Text style={s.sectionSubtotal}>
                Subtotal: {money(readyStockOrders.reduce((sum, o) => sum + Number(orderTotal(o) || 0), 0))}
              </Text>
            </View>
          )}
          {readyStockOrders.map((order, oi) => renderOrderGroup(order, oi, readyStockOrders.length))}
        </View>
      )}

      {/* Section 2: Production Orders */}
      {productionOrders.length > 0 && (
        <View>
          {isMixed && (
            <View wrap={false} style={s.sectionHeaderProd}>
              <Text style={s.sectionTitleProd}>
                PRODUCTION REQUIRED ({productionOrders.length} order{productionOrders.length === 1 ? '' : 's'})
              </Text>
              <Text style={s.sectionSubtotalProd}>
                Subtotal: {money(productionOrders.reduce((sum, o) => sum + Number(orderTotal(o) || 0), 0))}
              </Text>
            </View>
          )}
          {productionOrders.map((order, oi) => renderOrderGroup(order, oi, productionOrders.length))}
        </View>
      )}

      {/* Footer — order count and total price */}
      <View style={s.footerRow}>
        <Text style={s.footerLabel}>
          Total: {orders.length} order{orders.length === 1 ? '' : 's'}  ·  {money(orders.reduce((sum, o) => sum + Number(orderTotal(o) || 0), 0))}
        </Text>
      </View>

      {/* Page number */}
      <Text
        fixed
        style={s.pageFooter}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </Page>
  );
};

export default PrintSummaryPage;
